<script lang="ts">
	import {
		Alert,
		Button,
		ContextualNavigation,
		type DivScrollEvent,
		Drawer,
		findActiveMenuItem,
		handleSectionsScroll,
		Header,
		IconButton,
		MainLayout,
		MenuContainer,
		scrollToActiveMenuItem,
		Spinner,
		Text,
		Label,
		toggleActiveStateOnItems,
	} from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { LANG_DICT } from 'src/constants';
	import { format, setUpI18n } from 'src/services/i18n';
	import { change_lang_ios } from 'src/services/native_container_requests_ios';
	import { user_settings, user_data } from 'src/stores/account/user';
	import { app, os } from 'src/stores/session/native-bridge';
	import { initializing_language } from 'src/stores/session/preferences';
	import { drawerRef } from 'src/stores/ui/sidepanel-public';
	import { classMap, debounce } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { push, location } from 'svelte-spa-router';
	import type { ContextMenuItemPropsTypes } from '@Shelly/shelly-design-system/ContextualNavigation/ContextMenuItem/ContextMenuItem.types';
	import { applicationHostUrl } from 'src/helpers/account_helpers';
	import { createEventDispatcher } from 'svelte';
	import { postReq } from 'src/services/http';
	import LangDropdown from './HelperComponents/LangDropdown.svelte';

	//TODO Update this pages layout to be able to toggle between new and old layour. Maybe only drawer update ?
	export let menuItems: ContextMenuItemPropsTypes[];
	export let sectionsContent;
	export let latestVersion;
	export let title;
	export let mode: 'scroll' | 'single' = 'scroll';
	export let loadingContent = false;

	const dispatch = createEventDispatcher<{ 'section-change': string }>();

	$: isSingleMode = mode === 'single';
	$: activeSection = isSingleMode
		? (sectionsContent?.find((s) => s.id === findActiveMenuItem(menuItems)?.id) ??
			sectionsContent?.[0] ??
			null)
		: null;

	let host = import.meta.env.VITE_APP_BUCKET || window.location.host;

	// TODO: this is subject to a change if the component remains in use
	// For context see:
	// https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/merge_requests/3454#note_492062
	$: lang = $user_settings.lang
		? $user_settings.lang.length > 2
			? $user_settings.lang.slice(0, 2).toUpperCase()
			: $user_settings.lang.toUpperCase()
		: $initializing_language
			? $initializing_language.length > 2
				? $initializing_language.slice(0, 2).toUpperCase()
				: $initializing_language.toUpperCase()
			: 'EN';

	let showDropdownMenu = false;
	const changeLanguage = async (dropdownItem) => {
		showDropdownMenu = false;
		let newLang = Object.keys(LANG_DICT).filter((e) => e === dropdownItem.language)[0];
		if ($user_settings.lang !== newLang) {
			const activeLang = await setUpI18n(newLang);
			initializing_language.set(activeLang);
			user_settings.set({ ...$user_settings, lang: activeLang });

			/**
			 * Persist to backend so the user's profile keeps the new lang
			 * across token refreshes. Only meaningful for logged-in users —
			 * non-logged-in flows have no profile and no refresh cycle.
			 */
			if ($user_data?.email) {
				try {
					await postReq('/user/set_language', { lang: activeLang });
				} catch (error) {
					console.error('[DocumentContextualNavigation] set_language failed', error);
				}
			}

			if ($app) {
				if ($os === 'IOS') {
					change_lang_ios(activeLang);
				} else {
					// login_mobile_android($user_data.email, $user_data.user_api_url, lang);
				}
			}
		}
	};

	const dropdownItemsData = Object.entries(LANG_DICT).map((langObj) => {
		const subtitle =
			langObj[0].length > 2 ? langObj[0].slice(0, 2).toUpperCase() : langObj[0].toUpperCase();
		return {
			title: langObj[1],
			subtitle,
			language: langObj[0],
		};
	});

	let windowInnerWidth;
	let isScrollingFromMenu;
	let isScrolling;
	let isScrollingMobile;
	let ContextNavWrapperRef;
	let ContextNavWrapperMobileRef;
	const allSectionsRefs: { [key: string | number]: HTMLElement } = {};

	const stopIsScrolling = debounce(() => {
		let activeItem;

		activeItem = findActiveMenuItem(menuItems);

		if (isScrolling) {
			scrollToActiveMenuItem(activeItem.id, ContextNavWrapperRef);
		} else if (isScrollingMobile) {
			scrollToActiveMenuItem(activeItem.id, ContextNavWrapperMobileRef);
		}

		isScrolling = false;
		isScrollingFromMenu = false;
		isScrollingMobile = false;
	}, 50);

	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: isNotDekstop = windowInnerWidth <= SystemScreenBreakpoints.TABLET_MAX;

	const openContextualNavDrawer = () => {
		$drawerRef.open();
	};

	const onMainLayoutScroll = (e: DivScrollEvent) => {
		if (isSingleMode) return;
		isScrollingMobile = true;
		menuItems = handleSectionsScroll(
			e,
			isScrollingFromMenu,
			isScrollingMobile,
			stopIsScrolling,
			menuItems,
			Object.values(allSectionsRefs),
		);
	};
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<MainLayout
	noScrollPage={!isMobile}
	removePaddingTop
	removePaddingBottom
	backgroundImgSrc="/images/loginBackground.png"
	hideSlotDrawerMenu={!isNotDekstop}
	on:scroll={onMainLayoutScroll}
>
	<Header class="justify-content-center" withBackground slot="header">
		<div
			slot="leading"
			class="d-flex flex-wrap width-full align-items-center justify-content-center max-header-width"
		>
			<div class="flex-1">
				{#if !$user_data.email}
					<a href={applicationHostUrl(host)}>
						<img height="32" src="./images/shelly-logo.svg" alt="Shelly Logo" />
					</a>
				{:else}
					<img
						height="32"
						role="presentation"
						src="./images/shelly-logo.svg"
						alt="Shelly Logo"
						on:click={() => push('/')}
					/>
				{/if}
			</div>
			<div class="d-flex align-items-center">
				{#if !isMobile}
					{#if isNotDekstop}
						<IconButton
							on:click={openContextualNavDrawer}
							class="mr-s"
							icon="fa-solid fa-list"
							variant="secondary"
						>
							{$format('table_of_content', { default: 'Table of content' })}
						</IconButton>
					{/if}
					{#if !$user_data.email}
						<Button
							class="mr-s"
							size="m"
							icon="fa-solid fa-house"
							href={applicationHostUrl(host)}
							>{$format('go_to_shelly', { default: 'Go to Shelly' })}</Button
						>
					{:else}
						<Button class="mr-s" size="m" icon="fa-solid fa-house" on:click={() => push('/')}
							>{$format('go_to_shelly', { default: 'Go to Shelly' })}</Button
						>
					{/if}
				{:else if !$user_data.email}
					<IconButton
						aria-label={$format('button_go_to_shelly', {
							default: 'Button go to shelly',
						})}
						class="mr-s"
						size="m"
						icon="fa-solid fa-house"
						href={applicationHostUrl(host)}
					/>
				{:else}
					<IconButton
						aria-label={$format('button_go_to_shelly', {
							default: 'Button go to shelly',
						})}
						class="mr-s"
						size="m"
						icon="fa-solid fa-house"
						on:click={() => push('/')}
					/>
				{/if}
				<LangDropdown
					bind:showDropdown={showDropdownMenu}
					dropdownItems={dropdownItemsData}
					{lang}
					selectedLang={$user_settings.lang}
					on:change-lang={(e) => {
						changeLanguage(e.detail);
					}}
				/>
			</div>
			{#if isMobile}
				<IconButton
					class="flex-shrink-0 width-full mt-s"
					icon="fa-solid fa-list"
					variant="secondary"
					on:click={openContextualNavDrawer}
				>
					{$format('table_of_content', { default: 'Table of content' })}
				</IconButton>
			{/if}
		</div>
	</Header>

	<div class="d-flex height-full flex-direction-column align-items-center" slot="scroll">
		<MenuContainer
			bind:ref={ContextNavWrapperRef}
			hideSlotLeft={isNotDekstop}
			class={classMap({
				'max-page-content-width overflow-auto flex-1': true,
				'mb-l mt-l': !isMobile,
				'mb-m mt-m': isMobile,
			})}
			on:right-content-scroll={(e) => {
				if (isSingleMode) return;
				isScrolling = true;

				menuItems = handleSectionsScroll(
					e.detail,
					isScrollingFromMenu,
					isScrolling,
					stopIsScrolling,
					menuItems,
					Object.values(allSectionsRefs),
				);
			}}
		>
			<ContextualNavigation
				slot="left"
				style="width: 316px"
				on:click={(e) => {
					isScrollingFromMenu = true;
					menuItems = toggleActiveStateOnItems(menuItems, e.detail, allSectionsRefs[e.detail]);
					dispatch('section-change', e.detail);
				}}
				title={$format('table_of_content', { default: 'Table of content' })}
				{menuItems}
			/>

			<div slot="right">
				<Text class="mb-s" styleType="h1">{title}</Text>
				<Alert class="mb-l" heading={latestVersion} />

				{#if isSingleMode}
					{#if loadingContent}
						<div class="d-flex justify-content-center pt-l pb-l">
							<Spinner size="m" />
						</div>
					{:else if activeSection}
						<section id={activeSection.id}>
							<Text styleType="h2">{activeSection.title}</Text>
							<hr class="mt-xs mb-s hr-primary" />
							{#if activeSection.bolded}
								<Text styleType="h2" class="mb-m">{activeSection.bolded}</Text>
							{/if}

							{#if activeSection.titleDesc}
								{#each activeSection.titleDesc as titleDesc}
									<Text element="p" class="mb-m">{@html titleDesc}</Text>
								{/each}
							{/if}

							{#if activeSection.subtitles}
								{#each activeSection.subtitles as subtitle}
									<div class="d-flex align-items-center flex-wrap mb-m gap-s">
										<Text element="p" class="text-secondary mb-0">
											{subtitle.title}
										</Text>
										{#if subtitle.labels}
											<div class="d-inline-flex">
												{#each subtitle.labels as label}
													<Label
														icon={label.icon}
														iconColor={label.iconColor}
														type={label.type}
														size={label.size}
														value={label.value}
														textClass={label.textClass}
														class={label.class}
													/>
												{/each}
											</div>
										{/if}
									</div>
									{#each subtitle.description as desc}
										<Text element="p" class="mb-m ">{@html desc}</Text>
									{/each}
								{/each}
							{/if}
						</section>
					{/if}
				{:else}
					{#each sectionsContent as section, index}
						<section
							bind:this={allSectionsRefs[section.id]}
							class={classMap({
								'mb-l': index !== sectionsContent.length - 1,
							})}
							id={section.id}
						>
							<Text styleType="h2">{section.title}</Text>
							<hr class="mt-xs mb-s hr-primary" />
							{#if section.bolded}
								<Text styleType="h2" class="mb-m">{section.bolded}</Text>
							{/if}

							{#if section.titleDesc}
								{#each section.titleDesc as titleDesc}
									<Text element="p" class="mb-m">{@html titleDesc}</Text>
								{/each}
							{/if}

							{#if section.subtitles}
								{#each section.subtitles as subtitle}
									<div class="d-flex align-items-center flex-wrap mb-m gap-s">
										<Text element="p" class="text-secondary mb-0">
											{subtitle.title}
										</Text>
										{#if subtitle.labels}
											<div class="d-inline-flex">
												{#each subtitle.labels as label}
													<Label
														icon={label.icon}
														iconColor={label.iconColor}
														type={label.type}
														size={label.size}
														value={label.value}
														textClass={label.textClass}
														class={label.class}
													/>
												{/each}
											</div>
										{/if}
									</div>
									{#each subtitle.description as desc}
										<Text element="p" class="mb-m ">{@html desc}</Text>
									{/each}
								{/each}
							{/if}
						</section>
					{/each}
				{/if}
			</div>
		</MenuContainer>
		<hr class="mb-l width-full" />
		<div class="d-flex flex-wrap align-items-center justify-content-center pb-l" style="gap: 16px;">
			<Text
				class={!$location.startsWith('/terms-and-conditions') ? 'link-styles' : ''}
				on:click={() =>
					!$location.startsWith('/terms-and-conditions') && push('/terms-and-conditions')}
				>{$format('terms', { default: 'Terms and conditions' })}</Text
			>
			<Text
				class={!$location.startsWith('/privacy-policy') ? 'link-styles' : ''}
				on:click={() => !$location.startsWith('/privacy-policy') && push('/privacy-policy')}
				>{$format('privacy', { default: 'Privacy policy' })}</Text
			>
			<Text
				class={!$location.startsWith('/data-act') ? 'link-styles' : ''}
				on:click={() => !$location.startsWith('/data-act') && push('/data-act')}
				>{$format('data_act', { default: 'Data Act' })}</Text
			>
			<Text
				class={!$location.startsWith('/changelog') ? 'link-styles' : ''}
				on:click={() => !$location.startsWith('/changelog') && push('/changelog')}
				>{$format('tab_changelog', { default: 'Changelog' })}</Text
			>
		</div>
	</div>

	<Drawer
		bind:drawerHTMLRef={ContextNavWrapperMobileRef}
		bind:this={$drawerRef}
		slot="drawerMenu"
		heading={$format('table_of_content', { default: 'Table of content' })}
	>
		<div class="d-flex justifu-content-center">
			<ContextualNavigation
				on:click={(e) => {
					isScrollingFromMenu = true;
					menuItems = toggleActiveStateOnItems(menuItems, e.detail, allSectionsRefs[e.detail]);
					dispatch('section-change', e.detail);
					$drawerRef.close();
				}}
				{menuItems}
			/>
		</div>
	</Drawer>
</MainLayout>

<style>
	.max-header-width {
		max-width: 1122px;
	}

	:global(.max-page-content-width) {
		max-width: 1170px;
	}
</style>
